import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useHistory, useParams } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { samplerEditSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import Loader from "../../../../components/Loader";
import editSamplerSchema from "../../../../schemas/editSamplerSchema";
import EditSamplerForm from "../../../../forms/EditSamplerForm";
import { Select } from "antd";
const { Option } = Select;

const EditSampler = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: samplerEditSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();

  useEffect(() => {
    dispatch(actions.fetchSamplerDetails({ id: params.samplerId }));
    dispatch(actions.fetchStates());
    dispatch(actions.fetchAgencies());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, params.samplerId]);

  const samplerDetails = useSelector(selectors.selectSamplerDetails) || {};
  const states = useSelector(selectors.selectStates) || [];
  const districts = useSelector(selectors.selectDistricts) || [];
  const agencies = useSelector(selectors.selectAgencies) || [];
  const error = useSelector(selectors.selectError);
  const loading = useSelector(selectors.selectLoading);
  const districtsOfMultipleStates = useSelector(
    selectors.selectDistrictsOfMultipleStates
  );
  const [isLoadingOperatingDistricts, setIsLoadingOperatingDistricts] =
    useState(false);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Updating Company",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      name: samplerDetails?.name || "",
      samplerContact: samplerDetails?.samplerContact || "",
      address: samplerDetails?.address || "",
      pincode: samplerDetails?.pincode || "",
      email: samplerDetails?.email || "",
      state: samplerDetails?.state?._id || "",
      district: samplerDetails?.district?._id || "",
      agency: samplerDetails?.agency?._id || "",
      operatingStates:
        samplerDetails?.operatingStates?.map((state) => state._id) || [],
      operatingDistricts:
        samplerDetails?.operatingDistricts?.map((district) => district._id) ||
        [],
    },
    resolver: yupResolver(editSamplerSchema),
  });

  useEffect(() => {
    if (Object.keys(samplerDetails).length > 0) {
      setValue("name", samplerDetails.name);
      setValue("samplerContact", samplerDetails.samplerContact);
      setValue("address", samplerDetails.address);
      setValue("pincode", samplerDetails.pincode);
      setValue("email", samplerDetails.email);
      setValue("state", samplerDetails.state?._id);
      setValue("district", samplerDetails.district?._id);
      setValue("agency", samplerDetails.agency?._id);
      setValue(
        "operatingStates",
        samplerDetails?.operatingStates?.map((state) => state._id) || []
      );
      setValue(
        "operatingDistricts",
        samplerDetails?.operatingDistricts?.map((district) => district._id) ||
          []
      );
    }
  }, [samplerDetails]);

  const selectedState = watch("state");
  const selectedOperatingStates = watch("operatingStates");
  const selectedOperatingDistricts = watch("operatingDistricts");

  useEffect(() => {
    if (selectedState) {
      dispatch(actions.fetchDistricts({ stateId: selectedState }));
    }
  }, [dispatch, selectedState]);

  useEffect(() => {
    if (selectedOperatingStates && selectedOperatingStates.length > 0) {
      setIsLoadingOperatingDistricts(true);
      dispatch(
        actions.fetchDistrictsOfMultipleStates({
          stateIds: selectedOperatingStates,
          onSuccess: () => {
            setIsLoadingOperatingDistricts(false);
          },
          onFailure: () => {
            setIsLoadingOperatingDistricts(false);
          },
        })
      );
    } else {
      setValue("operatingDistricts", []);
    }
  }, [dispatch, selectedOperatingStates, setValue]);

  useEffect(() => {
    if (districtsOfMultipleStates && districtsOfMultipleStates.length > 0) {
      const validDistrictIds = districtsOfMultipleStates.map(
        (district) => district._id
      );
      const currentValue = selectedOperatingDistricts || [];
      const filteredOperatingDistricts = currentValue.filter((districtId) =>
        validDistrictIds.includes(districtId)
      );

      setValue("operatingDistricts", filteredOperatingDistricts);
    } else {
      setValue("operatingDistricts", []);
    }
  }, [dispatch, districtsOfMultipleStates, setValue]);

  const handleSuccess = () => {
    notification.success({
      message: "Sampler got edited successfully",
    });
    history.push(routes.backOffice.samplers.list);
  };

  const handleFailure = (error) => {
    notification.error({
      message: error?.message || "failed to edit sampler",
    });
  };

  const onSubmit = (data) => {
    const payloadData = {
      id: params.samplerId,
      data: {
        name: data.name,
        samplerContact: data.samplerContact,
        address: data.address,
        pincode: data.pincode,
        email: data.email,
        stateId: data.state,
        districtId: data.district,
        operatingStates: data.operatingStates,
        operatingDistricts: data.operatingDistricts,
        agencyId: data.agency,
      },
      onSuccess: handleSuccess,
      onFailure: handleFailure,
    };
    dispatch(actions.editSampler(payloadData));
  };
  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Edit Sampler"
        subTitle={samplerDetails.name}
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.samplers.list}>Cancel</Link>
          </Button>,
          <Button
            disabled={loading || isLoadingOperatingDistricts}
            loading={loading}
            key="2"
            type="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Update
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? <Loader /> : ""}
        {
          <EditSamplerForm
            options={{
              states: states.map((state) => (
                <Option key={state._id} value={state._id}>
                  {state.name}
                </Option>
              )),
              districts: districts.map((district) => (
                <Option key={district._id} value={district._id}>
                  {district.city}
                </Option>
              )),
              agencies: agencies.map((agency) => (
                <Option key={agency._id} value={agency._id}>
                  {agency.agencyName}
                </Option>
              )),
              districtsOfMultipleStates: districtsOfMultipleStates.map(
                (district) => (
                  <Option key={district._id} value={district._id}>
                    {district.districtName}
                  </Option>
                )
              ),
            }}
            control={control}
            setValue={setValue}
            watch={watch}
            errors={errors}
            trigger={trigger}
            isLoadingOperatingDistricts={isLoadingOperatingDistricts}
          />
        }
      </div>
    </div>
  );
};

export default EditSampler;
